<template>
  <div class="editor" ref="editor" :class="mode" v-keymap="keyScopes">
    <!---->
    <header class="content-heading-half">
      <properties-bar @showPreview="showPreview = !showPreview" @showArrangeView="showArrangeView = !showArrangeView"/>
    </header>
    <!---->
    <section class="content-main">
      <editor-layout :horizontalDisplay="horizontalDisplay" :showBottom="showBottom">
        <div slot="main">
          <Player ref="playerComponent" :video="video.src" class="editor__player" :class="{'editing': mode === 'editing'}" @ready="setPlayer" @loaded="onVideoLoaded" @timeupdate="onTimeupdate" @focus="onComponentFocus">
            <arrange-mode-layer v-if="showArrangeView" slot="overlay"/>
            <tag-layer v-else slot="overlay" class="editor__interaction-layer"  :time="currentTime" ref="overlay" />
            <default-toolbar v-if="!showArrangeView" slot="toolbar" />
            <arrange-mode-toolbar v-else slot="toolbar" />
          </Player>
        </div>
        <div slot="left" :style="metaStyle">
          <arrange-mode-left-panel v-if="showArrangeView"/>
          <metadata-form v-show="mode === 'editing' && !showArrangeView" @focus="onComponentFocus"/>
          <tag-list v-if="mode === 'playing' || mode === null" @focus="onComponentFocus" @input="onNewTag" />
        </div>
        <tracks-manager slot="bottom" class="editor__tracks" :time="currentTime" @focus="onComponentFocus" :isWideScreen="this.isWideScreen"/>
        <div v-if="isWideScreen" slot="toggle">
          <i v-if="!horizontalDisplay" class="material-icons" @click="horizontalDisplay=!horizontalDisplay">keyboard_arrow_right</i>
          <i v-else class="material-icons" @click="horizontalDisplay=!horizontalDisplay">keyboard_arrow_down</i>
        </div>
      </editor-layout>
    </section>
    <!---->
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex'
import Player from '../Player/core'
import PropertiesBar from './propertiesBar'
import TagLayer from './tagLayer'
import MetadataForm from './metadataForm'
import tagList from './tagList'
import TracksManager from './tracksManager'
import EditorLayout from './editorLayout'
import guid from '../../utils/guid'

import ArrangeModeLayer from './arrangeMode/arrangeModeLayer'
import ArrangeModeToolbar from './arrangeMode/arrangeModeToolbar'
import ArrangeModeLeftPanel from './arrangeMode/arrangeModeLeftPanel'
import DefaultToolbar from '../Player/toolbar'

export default {
  name: 'editor',

  components: {
    'Player': Player,
    'metadata-form': MetadataForm,
    'tag-list': tagList,
    'properties-bar': PropertiesBar,
    'tag-layer': TagLayer,
    'tracks-manager': TracksManager,
    'editor-layout': EditorLayout,
    'default-toolbar': DefaultToolbar,
    'arrange-mode-layer': ArrangeModeLayer,
    'arrange-mode-toolbar': ArrangeModeToolbar,
    'arrange-mode-left-panel': ArrangeModeLeftPanel
  },

  data: function () {
    return {
      currentTime: 0,
      metaHeight: 0,
      textFilter: null,
      showPreview: false,
      showArrangeView: false,
      horizontalDisplay: true,
      displayWidth: 0,
      clipBoard: {
        wp: null,
        track: null
      },
      keyScopes: [
        'editor',
        'properties-bar',
        'player',
        'metadata-form',
        'tracks-manager',
        'image-cropper'
      ]
    }
  },

  computed: {
    ...mapState(['player', 'video', 'selection', 'mode']),
    ...mapGetters(['videoDuration']),

    showBottom: function () {
      // return (this.video.tags && this.video.tags.length > 0) && !this.showArrangeView
      return !this.showArrangeView
    },

    isWideScreen: function () {
      return this.displayWidth >= 2550
    },

    selectedScene: function () {
      return this.selection.scene
    },

    timePosition: function () {
      return { left: `${this.videoDuration ? this.currentTime * 100 / this.videoDuration : 0}%` }
    },

    metaStyle: function () {
      let h = this.metaHeight + 'px'//this.mode === 'editing' ? this.metaHeight + 'px' : '100%'
      let mh = this.metaHeight + 'px'
      return {
        height: h,
        maxHeight: mh
      }
    },

    tags: function () {
      //console.log('tags', this.video.tags)
      const tags = this.video.tags
      return tags.filter((tag) => {
        return (tag.meta.title || '').toLowerCase().indexOf((this.textFilter || '').toLowerCase()) >= 0
      })
    }
  },

  watch: {
    'mode': 'onModeChanged',
    'selection': 'onSelectionChanged'
  },

  created() {
    // this.setInfoKey('editor.blank.create.tag')
    //console.log('editor created')
    window.addEventListener('resize', this.layoutChilds)

    this.keyEventUnsubscribe = this.keyEventSubscribe(['tracks-manager', 'player'], {
      'Space': this.onTogglePlay,
      'Escape': this.onDeselect,
      'Delete': this.onDelete,
      'ctrl+c': this.onCopy,
      'ctrl+x': this.onCut,
      'ctrl+v': this.onPaste
    });
  },

  mounted() {
    //console.log(this.unsubscriptions)
    this.$nextTick(this.layoutChilds)
  },

  beforeDestroy() {
    this.keyEventUnsubscribe()
    window.removeEventListener('resize', this.layoutChilds)
  },

   beforeRouteLeave (to, from, next) {
     if (from.name === 'editor') {
       this.resetEditor()
     }
     next()
  },

  methods: {
    ...mapMutations(['setPlayer', 'selectTag', 'setMode', 'selectScene', 'selectWaypoint', 'setInfoKey', 'deleteTag', 'copyTag', 'resetSelection', 'deleteWaypoint', 'copyWaypoint']),
    ...mapActions(['createTag', 'createScene']),

    onTimeupdate: function (currentTime) {
      this.currentTime = currentTime
    },

    onVideoLoaded() {
      //console.log("onVideoLoaded")
      this.layoutChilds()
    },

    onTogglePlay: function(e) {
      /* TODO: this is a hack to allow track name editing. It must be better handled */
      if(e.target.closest('.track__track-name')) {
        return
      }
      
      e.preventDefault();
      if (this.player.paused()) {
        this.player.play()
      } else {
        this.player.pause()
      }
    },

    onModeChanged: function (newVal, oldVal) {
      //console.log('onModeChanged', newVal, oldVal)
      if (newVal === 'editing' || oldVal === 'editing') {
        this.$nextTick(this.layoutChilds)
      }
    },

    onSelectionChanged: function () {
      this.layoutChilds()
    },

    onComponentFocus: function(value) {
      //console.log(value);
    },

    layoutChilds: function () {
      this.displayWidth = window.innerWidth
      //console.log('layoutChilds')
      // we want to match the height of the meta form to the height of the video editor (which can be smaller or taller than the meta form)
      // this is currently not possible with flex / flex-grid
      setTimeout(() => {
        if (this.$refs && this.$refs.playerComponent) {
          let pcel = this.$refs.playerComponent.$el
          let childs = pcel.children
          let height = 0
          for (var i = 0; i < childs.length; i++) {
            height += childs[i].getBoundingClientRect().height
          }
          this.metaHeight = height
        }
      }, 25)
    },

    onCopy: function () {
      if (this.selection.waypoint) {
        this.clipBoard.track = null;
        this.clipBoard.wp = this.selection.waypoint;
      } else if (this.selection.tag) {
        this.clipBoard.wp = null;
        this.clipBoard.track = this.selection.tag
      }
    },

    onCut: function () {
      if (this.selection.waypoint) {
        this.clipBoard.track = null;
        this.clipBoard.wp = this.selection.waypoint;
        this.deleteWaypoint(this.selection.waypoint)
      } else if (this.selection.tag) {
        this.clipBoard.wp = null;
        this.clipBoard.track = this.selection.tag;
        this.deleteTag(this.selection.tag);
      }
    },

    onPaste: function () {
      if (this.clipBoard.wp) {
        this.copyWaypoint(this.clipBoard.wp);
      } else if (this.clipBoard.track) {
        this.copyTag(this.clipBoard.track);
      }
    },

    onDelete: function () {
      if (this.selection.waypoint) {
        this.deleteWaypoint(this.selection.waypoint)
      } else if (this.selection.tag) {
        const options = {title: 'Delete track?', cancelLabel: 'Cancel'}
        this.$dialogs.confirm('Do you really want to delete this track?', options)
        .then((res) => {
          if (res.ok) {
            this.deleteTag(this.selection.tag);
            this.setMode(null)
          }
        })
      }
    },

    onDeselect: function () {
      this.resetSelection();
    },

    onNewTag: function(props) {
      //console.log("onNewTag")
      this.onCreateTag(props)
    },

    onCreateTag: function(props) {
      this.player.pause()
      this.pauseStateTrigger = this.pauseStateTrigger + 1
      let tag = this.createTag()
      this.selection.tag.template = props.key
      this.onCreateScene(props)
    },
  
    onCreateScene: function (props) {
      const time = this.player.currentTime()
      let wps = []
      for (var i = 0; i < props.waypoints; i++) {
        const wpTime = time + i * 3 < this.videoDuration ? time + i * 3 : this.videoDuration
        wps.push(this.createWaypoint(null, props, wpTime))
      }
      this.createScene(wps).then(newScene => { this.selectScene(newScene); this.selectWaypoint(wps[0]) }).catch(e => console.error('failed to create new scene', e))
    },

    createWaypoint: function (e, props, time) {
        const playerBB = this.$refs.overlay.$el.getBoundingClientRect()
        let x = 0.5
        let y = 0.5
        if (!props.x || !props.y) {
          x = (props.clientX - playerBB.left) / playerBB.width // 0.5
          y = (props.clientY - playerBB.top) / playerBB.height // 0.5
        }
     
        let w, h, pw, ph
        if (!props.bounds) {
          pw = 1
          ph = 1
        }
        // no aspect ratio wanted
        else if (props.bounds.wh) {
          let r = playerBB.width / playerBB.height
          w =  props.bounds.wh
          h =  props.bounds.wh
          // was commented out
          pw = props.bounds.wh / playerBB.width 
          ph = props.bounds.wh / playerBB.height
        // explicit sizw wanted
        } else if (props.bounds.w > 0) {
          w =  props.bounds.w
          h =  props.bounds.h
          pw = w / playerBB.width
          ph = h / playerBB.height
          // percent specified
        } else if (props.bounds.pw > 0) {
          pw = props.bounds.pw
          ph = props.bounds.ph
          w =  playerBB.width * pw
          h =  playerBB.height * ph
        }
        const waypoint = { time, x, y, w, h, pw, ph, id: guid() }
        return waypoint
      },

      /* bring editor to default state */
      resetEditor() {
        this.selectScene(null)
        this.setMode('playing')
        this.resetSelection()
      }
  }
}
</script>